/**
 * Pulso Reach contact form.
 *
 * Hosts a JotForm-embedded form inside the Contact section of the marketing
 * site. The form is loaded via iframe so all submission, validation, spam
 * protection, and inbox routing are handled on JotForm's side; we only
 * present it within our visual frame.
 *
 * To swap the form, edit JOTFORM_ID below (the path segment after
 * https://form.jotform.com/). The form should expose, at minimum, fields
 * for: name, email, organization, topic, and message; topic options can
 * mirror the `topics` array in i18n.ts.
 *
 * Until the JotForm ID is configured (placeholder below), the component
 * renders a graceful fallback: copy lifted from i18n + the GitHub link, so
 * the section never appears broken in production.
 */
import { useEffect, useRef, useState } from "react";
import { useT } from "../i18n/useT";

// Live JotForm ID for the Pulso Reach contact form.
// Form managed in JotForm; submissions delivered to hola@pulsoreach.org.
// To swap forms, replace this ID with the new form's path segment from
// https://form.jotform.com/<id>.
const JOTFORM_ID: string = "261621647849063";

export default function ContactForm() {
  const t = useT();
  const c = t.marketing.contact;
  const ref = useRef<HTMLIFrameElement | null>(null);
  const [loaded, setLoaded] = useState(false);

  // JotForm posts iframe-height messages so the embed can size to content.
  // We listen for them and update the iframe height to avoid an inner
  // scrollbar.
  useEffect(() => {
    if (!JOTFORM_ID) return;
    function onMsg(e: MessageEvent) {
      if (typeof e.data !== "string") return;
      // JotForm format: "setHeight:<px>:<formId>"
      const m = e.data.match(/^setHeight:(\d+):/);
      if (m && m[1] && ref.current) {
        ref.current.style.height = `${parseInt(m[1], 10)}px`;
      }
    }
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  return (
    <div className="mx-auto mt-10 max-w-2xl text-left">
      <p className="mb-6 text-sm text-slate-600">{c.formIntro}</p>

      {JOTFORM_ID ? (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <iframe
            ref={ref}
            title="Pulso Reach contact form"
            src={`https://form.jotform.com/${JOTFORM_ID}`}
            onLoad={() => setLoaded(true)}
            className="block w-full"
            style={{ minHeight: 640, border: 0 }}
            scrolling="no"
            allow="geolocation; microphone; camera; fullscreen"
          />
          {!loaded && (
            <div className="px-6 py-10 text-center text-sm text-slate-500">
              Loading form…
            </div>
          )}
        </div>
      ) : (
        <PlaceholderForm />
      )}

      <p className="mt-6 text-center text-xs text-slate-500">
        <a
          href={c.url}
          target="_blank"
          rel="noopener noreferrer"
          className="underline decoration-dotted underline-offset-4 hover:text-ink-900"
        >
          {c.githubFallback}
        </a>
      </p>
    </div>
  );
}

/**
 * Static, non-functional form skeleton shown only when JOTFORM_ID is empty.
 * Looks like a real form so the section is presentable; submit is disabled
 * with a helpful note pointing to email + GitHub.
 */
function PlaceholderForm() {
  const t = useT();
  const c = t.marketing.contact;
  const f = c.fields;
  return (
    <form
      onSubmit={(e) => e.preventDefault()}
      className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
      aria-label="Contact form (preview)"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={f.name} required>
          <input
            type="text"
            className="input"
            placeholder=""
            autoComplete="name"
            disabled
          />
        </Field>
        <Field label={f.email} required>
          <input
            type="email"
            className="input"
            placeholder=""
            autoComplete="email"
            disabled
          />
        </Field>
      </div>
      <Field label={f.org}>
        <input
          type="text"
          className="input"
          placeholder=""
          autoComplete="organization"
          disabled
        />
      </Field>
      <Field label={f.topic}>
        <select className="input" disabled defaultValue="">
          <option value="" disabled>
            —
          </option>
          {c.topics.map((tp) => (
            <option key={tp}>{tp}</option>
          ))}
        </select>
      </Field>
      <Field label={f.message}>
        <textarea rows={5} className="input resize-y" disabled />
      </Field>
      <div className="flex flex-col items-start gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs italic text-slate-500">
          Form embed pending: add your JotForm ID in
          {" "}
          <code className="rounded bg-slate-100 px-1 py-0.5">
            apps/web/src/components/ContactForm.tsx
          </code>
          .
        </p>
        <button
          type="submit"
          disabled
          className="inline-flex items-center gap-2 rounded-full bg-ink-900 px-5 py-2.5 text-sm font-semibold text-white opacity-60"
        >
          {f.submit}
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  required = false,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block text-sm">
      <span className="mb-1.5 inline-flex items-center gap-1 font-medium text-ink-900">
        {label}
        {required ? <span className="text-rose-500">*</span> : null}
      </span>
      {children}
    </label>
  );
}
